// Editor-written search copy for the fixed public pages. Pairs with
// `lib/mutations/page-seo.ts`. The head server reads the same endpoint to
// render crawled metadata; the SPA reads it so a page's tab title (and the
// GA4 `page_title` taken from it) is the title the head serves.

import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { isStaticPagePath } from '@leanin/shared/static-pages';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';
import { firstNonEmpty } from '../seo-text';
import { QUERY_KEY_REGISTRY } from './normalize';

export type PageSeoEntry = components['schemas']['PageSeoEntry'];
// An override as the admin read returns it: with the version the editor's
// save must name, so a save over someone else's newer copy is refused.
export type SavedPageSeo = components['schemas']['SavedPageSeo'];

// How long a read of the list is current. Past it, the next navigation to a
// static page rereads it in the background, so another editor's save reaches
// this tab within this long.
const PAGE_SEO_STALE_MS = 5 * 60_000;

// Bounded like every other read, by the client's request timeout
// (`setApiRequestTimeout`), which starts after the auth token is attached.
export async function fetchPageSeo(): Promise<ReadonlyArray<PageSeoEntry>> {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/page-seo')).pages;
}

async function fetchAdminPageSeo(): Promise<ReadonlyArray<SavedPageSeo>> {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/admin/page-seo')).pages;
}

export const pageSeoKeys = {
  all: ['page-seo'] as const,

  // The public read the static pages' tab titles come from. Seeded at boot
  // from the served shell when the first page is a static one
  // (`createAppQueryClient`), and patched in place by an editor's save
  // (`useUpdatePageSeo`). Kept for the session (nothing observes it, so the
  // default collection would drop it) and reread once stale; a failed read
  // keeps the last list read, and the next static page reads again.
  list: () =>
    queryOptions({
      queryKey: [...pageSeoKeys.all, 'list'] as const,
      queryFn: fetchPageSeo,
      staleTime: PAGE_SEO_STALE_MS,
      gcTime: Infinity,
    }),

  // The admin editor's read. Uncached and keyed apart from `list`, for the
  // reason `adminKeys.resourceEditor` is: the editor seeds its form once, so
  // it must seed from the server's row, never from the list a static page
  // read before another admin's save.
  editor: () =>
    queryOptions({
      queryKey: [...pageSeoKeys.all, 'editor'] as const,
      queryFn: fetchAdminPageSeo,
      staleTime: 0,
      gcTime: 0,
    }),
};

QUERY_KEY_REGISTRY.push(
  { name: 'pageSeoKeys.list', sampleKey: pageSeoKeys.list().queryKey },
  { name: 'pageSeoKeys.editor', sampleKey: pageSeoKeys.editor().queryKey },
);

export function usePageSeoEditor() {
  return useQuery(pageSeoKeys.editor());
}

// The editor's search title for `pathname`, from the cached list alone: null
// when it is not a static page, has no override or a blank one, or the list
// has not been read. The tab then falls back to the page's code subject, the
// precedence the head renders.
export function cachedStaticPageTitle(queryClient: QueryClient, pathname: string): string | null {
  if (!isStaticPagePath(pathname)) return null;
  const pages = queryClient.getQueryData(pageSeoKeys.list().queryKey);
  return firstNonEmpty(pages?.find((page) => page.path === pathname)?.seoTitle);
}

// Starts a read of the list unless the cache holds a current one, joining one
// already in flight. Never waited on: a navigation titles its tab from the
// cache at once, and the tab is retitled when the read lands
// (`subscribePageViews`). The query cache owns the read's lifecycle, and
// `prefetchQuery` settles without rejecting.
export function refreshPageSeo(queryClient: QueryClient): void {
  void queryClient.prefetchQuery(pageSeoKeys.list());
}

// Primes the list from the seed the head server embedded, before the router's
// first load reads it. Absent or invalid, nothing is primed.
export function seedPageSeo(
  queryClient: QueryClient,
  pages: ReadonlyArray<PageSeoEntry> | null,
): void {
  if (pages === null) return;
  queryClient.setQueryData(pageSeoKeys.list().queryKey, pages);
}
